
import React, {
  useEffect,
  useState,
  useCallback,
  Fragment,
} from "react";
import { observer } from "mobx-react-lite";
import { toast } from "react-toastify";
import { Popover, Transition } from "@headlessui/react";
import {
  PlusIcon,
  ChevronDownIcon,
  UsersIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import { useParams } from "react-router";

import { AddEditTarget } from "./add-edit-target";
import VoterReachOutListTarget from "./voter-reachoutListTarget";
import VoterReachOutTargetOverview from "./voter-reactout-target-overview";
import { useStore } from "../../../helpers/helpers";
import { classNames } from "../../../helpers/utils";
import SearchBar from "../../../components/SearchBar";
import { SideOverlay } from "../../../components/side-overlay";
import { DeleteModal } from "../../../components/DeleteModal";
import { CreateModel } from "../../../components/CreateModel";
import VoterReachOutSectionHeader from "../voter-reachout-section-header";

interface VoterReachOutTargetProps {
  projectid?: string;
}

export const VoterReachOutTarget = observer(
  (props: VoterReachOutTargetProps) => {
    const { projectid: propProjectId } = props;
    const { projectid: paramProjectId } =
      useParams<{ projectid: string }>();

    const projectid = propProjectId || paramProjectId;

    const [query, setQuery] = useState("");
    const [debouncedQuery, setDebouncedQuery] = useState("");
    const [isDeleteModalOpen, setIsDeleteModalOpen] =
      useState(false);
    const [isCreateModalOpen, setIsCreateModalOpen] =
      useState(false);
    const [deleteItem, setDeleteItem] =
      useState<string | null>(null);
    const [popOver, setPopOver] = useState(false);
    const [isLoadingDetails, setIsLoadingDetails] =
      useState(false);

    const { voterReachOutStore } = useStore();

    const {
      isMessageOverviewOpen,
      selectedOverViewItem,
      isEditOverlayOpen,
      targetSearchKeyword,
      setTargetSearchKeyword,
    } = voterReachOutStore;

    const datasetTypes = [
      {
        name: "Create Static",
        description: "Create a static target",
        key: "static",
        disabled: false,
      },
      {
        name: "Create Dynamic",
        description: "Create a dynamic target",
        key: "dynamic",
        disabled: true,
      },
    ];

    // Preserve the existing debounced API query behavior.
    useEffect(() => {
      const timer = setTimeout(() => {
        setDebouncedQuery(query);
      }, 500);

      return () => clearTimeout(timer);
    }, [query]);

    const getReachOutTarget = useCallback(() => {
      if (!projectid) return;

      voterReachOutStore.get_reachout_target({
        projectid,
        query: debouncedQuery,
      });
    }, [projectid, debouncedQuery, voterReachOutStore]);

    useEffect(() => {
      getReachOutTarget();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectid, debouncedQuery, selectedOverViewItem]);

    const addNewReachOut = useCallback(() => {
      voterReachOutStore.set_formType("new");
      voterReachOutStore.set_TargetFormValue({
        name: "",
        text: "",
      });
      voterReachOutStore.set_isEditOverlay(true);
      setIsCreateModalOpen(false);
    }, [voterReachOutStore]);

    const handleEdit = useCallback(
      async (item: { id: string }) => {
        setIsLoadingDetails(true);

        try {
          const res =
            await voterReachOutStore.details_reachout_target({
              id: item.id,
            });

          if (res?.data) {
            const val = res.data;

            voterReachOutStore.set_formType("edit");
            voterReachOutStore.set_TargetFormValue({
              id: val.id,
              name: val.name,
              text: val.text || val.msg?.text || "",
            });

            voterReachOutStore.set_isEditOverlay(true);
          }
        } catch (error) {
          toast.error("Failed to load target details");
        } finally {
          setIsLoadingDetails(false);
        }
      },
      [voterReachOutStore]
    );

    const handleOverlayClose = useCallback(() => {
      voterReachOutStore.set_isEditOverlay(false);
    }, [voterReachOutStore]);

    const deleteHandler = useCallback(async () => {
      if (!deleteItem) return;

      try {
        const result =
          await voterReachOutStore.delete_reachout_target({
            id: deleteItem,
          });

        if (result.status === 200) {
          toast.success("Target deleted successfully");

          if (
            voterReachOutStore.selectedOverViewItem?.id ===
            deleteItem
          ) {
            voterReachOutStore.set_IsMessageOverviewOpen(false);
            voterReachOutStore.set_selected_overview_item(null);
          }

          voterReachOutStore.reachOutTargetList =
            voterReachOutStore.reachOutTargetList.filter(
              (item: any) => item.id !== deleteItem
            );

          voterReachOutStore.lastReachOutTargetPayload = null;
          getReachOutTarget();
        } else {
          toast.error("Failed to delete target");
        }
      } catch (error) {
        toast.error(
          "An error occurred while deleting the target"
        );
      } finally {
        setIsDeleteModalOpen(false);
        setDeleteItem(null);
      }
    }, [deleteItem, voterReachOutStore, getReachOutTarget]);

    const header = () => (
      <VoterReachOutSectionHeader
        title="Target Groups"
        description="Manage the voter groups included in your outreach."
        icon={UsersIcon}
        action={
          <Popover className="relative shrink-0">
            {({ open }) => (
              <>
                <Popover.Button
                  onClick={() => setPopOver(!popOver)}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto"
                >
                  <PlusIcon className="h-4 w-4" />
                  <span>Add New</span>
                  <ChevronDownIcon
                    className={classNames(
                      "h-4 w-4 transition-transform",
                      open ? "rotate-180" : ""
                    )}
                  />
                </Popover.Button>

                <Transition
                  show={popOver}
                  as={Fragment}
                  enter="transition ease-out duration-150"
                  enterFrom="opacity-0 translate-y-1"
                  enterTo="opacity-100 translate-y-0"
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100 translate-y-0"
                  leaveTo="opacity-0 translate-y-1"
                >
                  <Popover.Panel
                    static
                    className="absolute right-0 z-50 mt-2 w-64 origin-top-right"
                  >
                    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white p-1.5 shadow-lg">
                      {datasetTypes.map((item) => (
                        <button
                          key={item.key}
                          type="button"
                          disabled={item.disabled}
                          onClick={() => {
                            if (
                              !item.disabled &&
                              item.key === "static"
                            ) {
                              setIsCreateModalOpen(true);
                            }

                            setPopOver(false);
                          }}
                          className={classNames(
                            "w-full rounded-lg px-3 py-2.5 text-left transition-colors",
                            item.disabled
                              ? "cursor-not-allowed text-slate-400"
                              : "text-slate-700 hover:bg-slate-50"
                          )}
                        >
                          <span className="flex items-center justify-between gap-2">
                            <span className="text-sm font-medium">
                              {item.name}
                            </span>

                            {item.disabled && (
                              <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500">
                                Coming soon
                              </span>
                            )}
                          </span>

                          <span className="mt-1 block text-xs text-slate-500">
                            {item.description}
                          </span>
                        </button>
                      ))}
                    </div>
                  </Popover.Panel>
                </Transition>
              </>
            )}
          </Popover>
        }
      >
        <div className="px-5 py-3.5 sm:px-6">
          <div className="relative">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-slate-400" />

            <div className="[&_input]:!rounded-lg [&_input]:!border-slate-200 [&_input]:!bg-slate-50 [&_input]:!py-2.5 [&_input]:!pl-9 [&_input]:!text-sm [&_input]:!shadow-none [&_input]:focus:!border-indigo-400 [&_input]:focus:!bg-white [&_input]:focus:!ring-2 [&_input]:focus:!ring-indigo-100">
              <SearchBar
                query={targetSearchKeyword}
                handleChange={setTargetSearchKeyword}
                handleKeyDown={() => {}}
                getFilteredData={() => {}}
                placeholder="Search target groups..."
              />
            </div>
          </div>
        </div>
      </VoterReachOutSectionHeader>
    );

    if (!projectid) {
      return (
        <div className="rounded-xl border border-slate-200 bg-white px-5 py-10 text-center">
          <p className="text-sm font-medium text-slate-700">
            Project ID is required
          </p>
        </div>
      );
    }

    return (
      <div className="min-h-full space-y-4 pb-6">
        <div className="mt-4">{header()}</div>

        {isLoadingDetails && (
          <div
            role="status"
            className="flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600"
          >
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-200 border-t-indigo-600" />
            Loading target details...
          </div>
        )}

        <VoterReachOutListTarget
          setPopOver={setPopOver}
          deleteMessage={(id) => {
            setIsDeleteModalOpen(true);
            setDeleteItem(id);
          }}
          editMessage={handleEdit}
        />

        <SideOverlay
          show={isEditOverlayOpen}
          onClose={handleOverlayClose}
          title={
            voterReachOutStore.formType === "new"
              ? "Create Target"
              : "Edit Target"
          }
        >
          <AddEditTarget getReachOutTarget={getReachOutTarget} />
        </SideOverlay>

        <SideOverlay
          show={isMessageOverviewOpen}
          onClose={() =>
            voterReachOutStore.set_IsMessageOverviewOpen(false)
          }
          title={selectedOverViewItem?.name || "Target Details"}
        >
          <VoterReachOutTargetOverview
            data={selectedOverViewItem}
          />
        </SideOverlay>

        {isDeleteModalOpen && (
          <DeleteModal
            isOpen={isDeleteModalOpen}
            desc="Are you sure you want to delete this target? This action cannot be undone."
            onDeleteClick={deleteHandler}
            onCancelClick={() => {
              setIsDeleteModalOpen(false);
              setDeleteItem(null);
            }}
          />
        )}

        {isCreateModalOpen && (
          <CreateModel
            isOpen={isCreateModalOpen}
            desc="Create a new static target group?"
            onCreateClick={addNewReachOut}
            onCancelClick={() => setIsCreateModalOpen(false)}
          />
        )}
      </div>
    );
  }
);
